import { apiClient } from './apiClient';
import logger from '../../lib/logger';

// Types
export interface GoogleConfigStatus {
  configured: boolean;
  message: string;
}

export interface GoogleAuthStatus {
  connected: boolean;
  expired?: boolean;
  expiresAt?: string;
  scopes?: string[];
  message?: string;
}

export interface GoogleAuthUrl {
  authUrl: string;
}

export interface FilePermission {
  email: string;
  name: string;
  photo?: string;
  role: string;
}

export interface SharingUser {
  email: string;
  name: string;
  photo?: string;
}

export interface Collaborator {
  email: string;
  name: string;
  photo?: string;
  isOwner: boolean;
  fileCount: number;
}

export interface DriveFile {
  id: string;
  name: string;
  type: string;
  mimeType: string;
  size: string;
  modified: string;
  thumbnailLink?: string;
  webViewLink?: string;
  isFolder: boolean;
  folderColorRgb?: string;
  owner?: string;
  ownerEmail?: string;
  ownerPhoto?: string;
  trashedTime?: string;
  permissions?: FilePermission[];
  sharingUser?: SharingUser;
  starred?: boolean;
}

export interface SharedDrive {
  id: string;
  name: string;
  createdTime: string;
  colorRgb?: string;
  backgroundImageLink?: string;
  backgroundImageBase64?: string;
  memberCount?: number;
  organizationName?: string;
}

export interface DriveFolder {
  id: string;
  name: string;
  modifiedTime: string;
  parents?: string[];
  isSharedDrive?: boolean;
}

export interface DriveUser {
  displayName: string;
  email: string;
  photoLink?: string;
  permissionId?: string;
  domain: string;
  isGoogleWorkspace: boolean;
  organization?: string;
}

// Google OAuth Service
export const googleService = {
  // Check if Google OAuth is configured on backend
  async getConfigStatus(): Promise<GoogleConfigStatus> {
    const response = await apiClient.get<GoogleConfigStatus>('/google/config-status');
    return response.data;
  },

  // Get OAuth authorization URL
  async getAuthUrl(): Promise<string> {
    const response = await apiClient.get<GoogleAuthUrl>('/google/auth');
    return response.data.authUrl;
  },

  // Check if user has connected Google account
  async getStatus(): Promise<GoogleAuthStatus> {
    const response = await apiClient.get<GoogleAuthStatus>('/google/status');
    return response.data;
  },

  // Disconnect Google account
  async disconnect(): Promise<void> {
    await apiClient.delete('/google/disconnect');
  },

  // Start OAuth flow - opens popup or redirects
  async startOAuthFlow(): Promise<void> {
    const authUrl = await this.getAuthUrl();
    // Open in same window for proper redirect handling
    window.location.href = authUrl;
  },
};

// Google Drive Service
export const driveService = {
  // Get current Drive user info
  async getUserInfo(): Promise<DriveUser> {
    const response = await apiClient.get<DriveUser>('/google/drive/user');
    return response.data;
  },

  // List folders in a directory
  async listFolders(parentId: string = 'root', includeSharedDrives: boolean = false): Promise<DriveFolder[]> {
    const response = await apiClient.get<{ folders: DriveFolder[] }>('/google/drive/folders', {
      params: { parentId, ...(includeSharedDrives ? { includeSharedDrives: 'true' } : {}) },
    });
    return response.data.folders;
  },

  // List files in a folder (supports shared drives via driveId)
  async listFiles(folderId: string = 'root', driveId?: string): Promise<DriveFile[]> {
    const params: Record<string, string> = { folderId };
    if (driveId) {
      params.driveId = driveId;
    }
    const response = await apiClient.get<{ files: DriveFile[] }>('/google/drive/files', {
      params,
    });
    return response.data.files;
  },

  // List files with pagination support (returns nextPageToken for infinite scroll)
  async listFilesPaged(folderId: string = 'root', driveId?: string, pageToken?: string): Promise<{ files: DriveFile[]; nextPageToken?: string }> {
    const params: Record<string, string> = { folderId };
    if (driveId) params.driveId = driveId;
    if (pageToken) params.pageToken = pageToken;
    const response = await apiClient.get<{ files: DriveFile[]; nextPageToken?: string }>('/google/drive/files', { params });
    return { files: response.data.files, nextPageToken: response.data.nextPageToken };
  },

  // List files by mimeType filter (searches all folders)
  async listFilesByType(mimeTypes: string[]): Promise<DriveFile[]> {
    const response = await apiClient.get<{ files: DriveFile[] }>('/google/drive/files-by-type', {
      params: { mimeTypes: mimeTypes.join(',') },
    });
    return response.data.files;
  },

  // Get file metadata
  async getFile(fileId: string): Promise<DriveFile> {
    const response = await apiClient.get<{ file: DriveFile }>(`/google/drive/file/${fileId}`);
    return response.data.file;
  },

  // Get download URL
  getDownloadUrl(fileId: string): string {
    const baseUrl = import.meta.env.VITE_API_BASE_URL ||
      (import.meta.env.PROD ? '/api' : 'http://localhost:3001/api');
    return `${baseUrl}/google/drive/download/${fileId}`;
  },

  // Download file as blob and convert to File object
  async downloadAsFile(fileId: string, fileName: string, mimeType: string): Promise<File> {
    const response = await apiClient.get(`/google/drive/download/${fileId}`, {
      responseType: 'blob',
    });
    return new File([response.data], fileName, { type: mimeType });
  },

  // Download multiple files (legacy - all at once)
  async downloadMultipleAsFiles(files: { id: string; name: string; mimeType: string }[]): Promise<File[]> {
    const downloadPromises = files.map(file =>
      this.downloadAsFile(file.id, file.name, file.mimeType)
    );
    return Promise.all(downloadPromises);
  },

  // Download single file with retry
  async downloadWithRetry(
    fileId: string,
    fileName: string,
    mimeType: string,
    maxRetries: number = 3
  ): Promise<{ success: true; file: File } | { success: false; error: string }> {
    let lastError = '';
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const file = await this.downloadAsFile(fileId, fileName, mimeType);
        return { success: true, file };
      } catch (err: any) {
        lastError = err.response?.data?.error || err.message || 'Download failed';
        logger.warn(`Download attempt ${attempt}/${maxRetries} failed for ${fileName}:`, lastError);
        if (attempt < maxRetries) {
          // Wait before retry (exponential backoff: 1s, 2s, 4s)
          await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, attempt - 1)));
        }
      }
    }
    return { success: false, error: lastError };
  },

  // Download multiple files with batching and progress
  async downloadMultipleWithProgress(
    files: { id: string; name: string; mimeType: string }[],
    onProgress: (fileIndex: number, status: 'processing' | 'complete' | 'error', error?: string) => void,
    concurrency: number = 5
  ): Promise<{ downloadedFiles: File[]; errors: { index: number; name: string; error: string }[] }> {
    const downloadedFiles: File[] = [];
    const errors: { index: number; name: string; error: string }[] = [];

    // Process files in batches
    for (let i = 0; i < files.length; i += concurrency) {
      const batch = files.slice(i, Math.min(i + concurrency, files.length));
      const batchStartIndex = i;

      // Mark all files in batch as processing
      batch.forEach((_, batchIdx) => {
        onProgress(batchStartIndex + batchIdx, 'processing');
      });

      // Download batch in parallel
      const results = await Promise.all(
        batch.map(async (file, batchIdx) => {
          const fileIndex = batchStartIndex + batchIdx;
          const result = await this.downloadWithRetry(file.id, file.name, file.mimeType);

          if (result.success) {
            onProgress(fileIndex, 'complete');
            return { index: fileIndex, file: result.file, error: null };
          } else {
            onProgress(fileIndex, 'error', result.error);
            return { index: fileIndex, file: null, error: result.error, name: file.name };
          }
        })
      );

      // Collect results
      results.forEach(result => {
        if (result.file) {
          downloadedFiles.push(result.file);
        } else if (result.error) {
          errors.push({ index: result.index, name: result.name!, error: result.error });
        }
      });
    }

    return { downloadedFiles, errors };
  },

  // Delete files
  async deleteFiles(fileIds: string[]): Promise<{ success: boolean; message: string }> {
    const response = await apiClient.delete<{ success: boolean; message: string; results: any[] }>(
      '/google/drive/files',
      { data: { fileIds } }
    );
    return response.data;
  },

  // Build breadcrumb path from folder ID
  async getBreadcrumbPath(folderId: string): Promise<{ id: string; name: string }[]> {
    const path: { id: string; name: string }[] = [];
    let currentId = folderId;

    while (currentId && currentId !== 'root') {
      try {
        const file = await this.getFile(currentId);
        path.unshift({ id: file.id, name: file.name });
        // Note: Would need parents field to continue up the tree
        break; // For now, just get current folder
      } catch {
        break;
      }
    }

    return path;
  },

  // ============ NEW SIDEBAR METHODS ============

  // Get shared drives (team drives)
  async getSharedDrives(): Promise<SharedDrive[]> {
    const response = await apiClient.get<{ drives: SharedDrive[] }>('/google/drive/shared-drives');
    return response.data.drives;
  },

  // Get files shared with me
  async getSharedWithMe(pageSize: number = 50): Promise<DriveFile[]> {
    const response = await apiClient.get<{ files: DriveFile[] }>('/google/drive/shared-with-me', {
      params: { pageSize },
    });
    return response.data.files;
  },

  // Get recent files
  async getRecent(limit: number = 20): Promise<DriveFile[]> {
    const response = await apiClient.get<{ files: DriveFile[] }>('/google/drive/recent', {
      params: { limit },
    });
    return response.data.files;
  },

  // Get starred files
  async getStarred(): Promise<DriveFile[]> {
    const response = await apiClient.get<{ files: DriveFile[] }>('/google/drive/starred');
    return response.data.files;
  },

  // Get trash files
  async getTrash(): Promise<DriveFile[]> {
    const response = await apiClient.get<{ files: DriveFile[] }>('/google/drive/trash');
    return response.data.files;
  },

  // Toggle star on file
  async toggleStar(fileId: string, starred: boolean): Promise<void> {
    await apiClient.patch(`/google/drive/file/${fileId}/star`, { starred });
  },

  // Restore file from trash
  async restoreFromTrash(fileId: string): Promise<void> {
    await apiClient.patch(`/google/drive/file/${fileId}/restore`);
  },

  // Delete file permanently (from trash)
  async deletePermanently(fileId: string): Promise<void> {
    await apiClient.delete(`/google/drive/files`, { data: { fileIds: [fileId] } });
  },

  // Empty trash
  async emptyTrash(): Promise<void> {
    await apiClient.delete('/google/drive/trash');
  },

  // Check status of multiple files (trashed/deleted)
  async checkFilesStatus(fileIds: string[]): Promise<Record<string, 'ok' | 'trashed' | 'deleted'>> {
    const response = await apiClient.post<{ statuses: Record<string, 'ok' | 'trashed' | 'deleted'> }>(
      '/google/drive/check-files-status',
      { fileIds }
    );
    return response.data.statuses;
  },

  // ============ FILE OPERATIONS ============

  // Rename file
  async renameFile(fileId: string, name: string): Promise<{ id: string; name: string }> {
    const response = await apiClient.patch<{ success: boolean; file: { id: string; name: string } }>(
      `/google/drive/file/${fileId}/rename`,
      { name }
    );
    return response.data.file;
  },

  // Copy file
  async copyFile(fileId: string, name?: string, parentId?: string): Promise<{ id: string; name: string; mimeType: string; webViewLink: string }> {
    const response = await apiClient.post<{ success: boolean; file: { id: string; name: string; mimeType: string; webViewLink: string } }>(
      `/google/drive/file/${fileId}/copy`,
      { name, parentId }
    );
    return response.data.file;
  },

  // Move file to another folder
  async moveFile(fileId: string, newParentId: string): Promise<{ id: string; name: string; parents: string[] }> {
    const response = await apiClient.patch<{ success: boolean; file: { id: string; name: string; parents: string[] } }>(
      `/google/drive/file/${fileId}/move`,
      { newParentId }
    );
    return response.data.file;
  },

  // Move to trash (soft delete)
  async moveToTrash(fileId: string): Promise<void> {
    await apiClient.patch(`/google/drive/file/${fileId}/trash`);
  },

  // Get file permissions
  async getPermissions(fileId: string): Promise<{ id: string; type: string; role: string; emailAddress?: string; displayName?: string }[]> {
    const response = await apiClient.get<{ permissions: { id: string; type: string; role: string; emailAddress?: string; displayName?: string }[] }>(
      `/google/drive/file/${fileId}/permissions`
    );
    return response.data.permissions;
  },

  // Add permission (share with user)
  async addPermission(fileId: string, email: string, role: 'reader' | 'writer' | 'commenter', sendNotification?: boolean): Promise<{ id: string; role: string; emailAddress: string }> {
    const response = await apiClient.post<{ success: boolean; permission: { id: string; role: string; emailAddress: string } }>(
      `/google/drive/file/${fileId}/permissions`,
      { email, role, type: 'user', sendNotification }
    );
    return response.data.permission;
  },

  // Remove permission
  async removePermission(fileId: string, permissionId: string): Promise<void> {
    await apiClient.delete(`/google/drive/file/${fileId}/permissions/${permissionId}`);
  },

  // Get shareable link
  async getShareLink(fileId: string): Promise<{ link: string; downloadLink?: string }> {
    const response = await apiClient.get<{ success: boolean; link: string; downloadLink?: string }>(
      `/google/drive/file/${fileId}/share-link`
    );
    return { link: response.data.link, downloadLink: response.data.downloadLink };
  },

  // Set folder color
  async setFolderColor(folderId: string, color: string): Promise<{ id: string; name: string; folderColorRgb: string }> {
    const response = await apiClient.patch<{ success: boolean; folder: { id: string; name: string; folderColorRgb: string } }>(
      `/google/drive/folder/${folderId}/color`,
      { color }
    );
    return response.data.folder;
  },

  // Create shortcut
  async createShortcut(fileId: string, parentId?: string): Promise<{ id: string; name: string }> {
    const response = await apiClient.post<{ success: boolean; shortcut: { id: string; name: string } }>(
      `/google/drive/file/${fileId}/shortcut`,
      { parentId }
    );
    return response.data.shortcut;
  },

  // Search files
  async search(query: string, pageSize: number = 50): Promise<{ files: DriveFile[]; query: string }> {
    const response = await apiClient.get<{ files: DriveFile[]; query: string }>('/google/drive/search', {
      params: { q: query, pageSize },
    });
    return response.data;
  },

  // Get list of collaborators
  async getCollaborators(): Promise<Collaborator[]> {
    const response = await apiClient.get<{ collaborators: Collaborator[] }>('/google/drive/collaborators');
    return response.data.collaborators;
  },

  // ============ FILE UPLOAD ============

  // Upload single file to Google Drive
  async uploadFile(file: File, folderId: string = 'root', onProgress?: (progress: number) => void): Promise<{ id: string; name: string; mimeType: string; webViewLink?: string }> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folderId', folderId);

    const response = await apiClient.post<{ success: boolean; file: { id: string; name: string; mimeType: string; size: string; webViewLink?: string } }>(
      '/google/drive/upload',
      formData,
      {
        // DON'T set Content-Type for FormData - let browser set it with boundary
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress(progress);
          }
        },
      }
    );
    return response.data.file;
  },

  // Upload multiple files to Google Drive
  async uploadFiles(files: File[], folderId: string = 'root', onProgress?: (completed: number, total: number) => void): Promise<{ uploaded: number; total: number; results: Array<{ success: boolean; originalName: string; file?: { id: string; name: string }; error?: string }> }> {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file);
    });
    formData.append('folderId', folderId);

    const response = await apiClient.post<{ success: boolean; uploaded: number; total: number; results: Array<{ success: boolean; originalName: string; file?: { id: string; name: string; mimeType: string }; error?: string }> }>(
      '/google/drive/upload-multiple',
      formData
      // DON'T set Content-Type for FormData - let browser set it with boundary
    );
    return response.data;
  },

  // Create folder in Google Drive
  async createFolder(name: string, parentId: string = 'root'): Promise<{ id: string; name: string; mimeType: string; webViewLink?: string }> {
    const response = await apiClient.post<{ success: boolean; folder: { id: string; name: string; mimeType: string; webViewLink?: string } }>(
      '/google/drive/folder',
      { name, parentId }
    );
    return response.data.folder;
  },
};

export default { googleService, driveService };
